import {
  createAuthenticatedAppearancePreferencesRepository,
} from "./account/appearance-preferences-repository.js";
import {
  browserAccountStateSessionRepository,
} from "./account/account-state-session-repository.js";
import {
  createBrowserDomainColorBootCacheProjectionProvider,
} from "./domain-color-preference-local-provider.js";
import {
  browserLocalDomainColorPreferenceRepository,
  domainColorPreferenceDecision,
  encodeDomainColorBootCache,
} from "./domain-color-preference-repository.js";
import {
  createBrowserThemeBootCacheProjectionProvider,
} from "./theme-local-provider.js";
import {
  browserLocalThemePreferenceRepository,
  encodeThemeBootCache,
  themePreferenceDecision,
} from "./theme-preference-repository.js";

const SYSTEM_SELECTION = Object.freeze({ kind: "system" });

function hasMethods(value, names) {
  return names.every((name) => typeof value?.[name] === "function");
}

function statesEqual(left, right) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function freezeState(value) {
  const next = { ...value };
  if (next.authoritative) {
    next.authoritative = Object.freeze({ ...next.authoritative });
  }
  if (next.bootCache) next.bootCache = Object.freeze({ ...next.bootCache });
  return Object.freeze(next);
}

function cacheFailure(state, effect) {
  if (effect?.kind !== "unavailable") return freezeState(state);
  return freezeState({
    ...state,
    bootCache: {
      kind: "unavailable",
      operation: effect.operation,
      reason: effect.reason,
    },
  });
}

function themeConflict(remoteState, projection) {
  return freezeState({
    kind: "conflict",
    expectedRevision: remoteState.expectedRevision,
    actualRevision: projection.kind === "ready" ? projection.revision : null,
    selection: projection.kind === "ready"
      ? projection.selection
      : SYSTEM_SELECTION,
    authoritative: projection,
  });
}

function domainColorConflict(remoteState, projection) {
  return freezeState({
    kind: "conflict",
    expectedRevision: remoteState.expectedRevision,
    actualRevision: projection.kind === "ready" ? projection.revision : null,
    authoritative: projection,
  });
}

function pendingChild(remoteState, projection, previous) {
  return freezeState({
    kind: "pending",
    operation: remoteState.operation,
    authoritative: projection,
    ...(previous?.bootCache ? { bootCache: previous.bootCache } : {}),
  });
}

function boundaryChild(remoteState) {
  return freezeState({
    kind: remoteState.kind,
    ...(remoteState.operation ? { operation: remoteState.operation } : {}),
    ...(remoteState.reason ? { reason: remoteState.reason } : {}),
  });
}

export function createAccountAwareAppearancePreferencesRepository({
  localThemeRepository,
  localDomainColorRepository,
  authenticatedRepository,
  themeBootCacheProvider,
  domainColorBootCacheProvider,
} = {}) {
  if (
    !hasMethods(localThemeRepository, [
      "followSystemTheme",
      "load",
      "selectNamedTheme",
      "setSelection",
      "state",
      "subscribe",
    ])
    || !hasMethods(localDomainColorRepository, [
      "load",
      "setMode",
      "state",
      "subscribe",
    ])
    || !hasMethods(authenticatedRepository, [
      "load",
      "setDomainColors",
      "setTheme",
      "state",
      "subscribe",
    ])
    || !hasMethods(themeBootCacheProvider, ["remove", "writeReady"])
    || !hasMethods(domainColorBootCacheProvider, ["remove", "writeReady"])
  ) {
    throw new TypeError(
      "Appearance preferences require their exact local, account, and cache boundaries.",
    );
  }

  let authority = "unknown";
  let hadKnownAuthority = false;
  let suppressLocalProjection = false;
  let disposed = false;
  let themeState = freezeState({ kind: "uninitialized" });
  let domainColorState = freezeState({ kind: "uninitialized" });
  const themeListeners = new Set();
  const domainColorListeners = new Set();

  function deliver(listeners, state, label) {
    for (const listener of listeners) {
      try {
        listener(state);
      } catch (_error) {
        globalThis.reportError?.(new Error(`${label} subscriber failed.`));
      }
    }
  }

  function publish(nextTheme, nextDomainColors) {
    if (disposed) return;
    const frozenTheme = freezeState(nextTheme);
    const frozenDomainColors = freezeState(nextDomainColors);
    const themeChanged = !statesEqual(themeState, frozenTheme);
    const domainColorsChanged = !statesEqual(
      domainColorState,
      frozenDomainColors,
    );
    themeState = frozenTheme;
    domainColorState = frozenDomainColors;
    if (themeChanged) deliver(themeListeners, themeState, "Theme preference");
    if (domainColorsChanged) {
      deliver(
        domainColorListeners,
        domainColorState,
        "Domain-color preference",
      );
    }
  }

  function projectThemeCache(state) {
    if (state.kind === "pending") return freezeState(state);
    let projection = state;
    if (state.kind === "conflict") {
      projection = state.authoritative ?? (
        Number.isSafeInteger(state.actualRevision) && state.actualRevision > 0
          ? {
            kind: "ready",
            revision: state.actualRevision,
            selection: state.selection,
          }
          : { kind: "missing" }
      );
    }
    const effect = projection?.kind === "ready"
      ? themeBootCacheProvider.writeReady(encodeThemeBootCache(projection))
      : themeBootCacheProvider.remove();
    return cacheFailure(state, effect);
  }

  function projectDomainColorCache(state) {
    if (state.kind === "pending") return freezeState(state);
    const projection = state.kind === "conflict" ? state.authoritative : state;
    const effect = projection?.kind === "ready"
      ? domainColorBootCacheProvider.writeReady(
        encodeDomainColorBootCache(projection),
      )
      : domainColorBootCacheProvider.remove();
    return cacheFailure(state, effect);
  }

  function publishKnown(nextTheme, nextDomainColors) {
    hadKnownAuthority = true;
    publish(
      projectThemeCache(nextTheme),
      projectDomainColorCache(nextDomainColors),
    );
  }

  function activateLocal() {
    authority = "local";
    suppressLocalProjection = true;
    let nextTheme;
    let nextDomainColors;
    try {
      nextTheme = localThemeRepository.load();
    } catch (_error) {
      nextTheme = {
        kind: "unavailable",
        operation: "load",
        reason: "local-theme-load-failed",
      };
    }
    try {
      nextDomainColors = localDomainColorRepository.load();
    } catch (_error) {
      nextDomainColors = {
        kind: "unavailable",
        operation: "load",
        reason: "local-domain-color-load-failed",
      };
    }
    suppressLocalProjection = false;
    publishKnown(nextTheme, nextDomainColors);
  }

  function projectBoundary(remoteState) {
    authority = "unknown";
    const removeCache = !["uninitialized", "loading"].includes(remoteState.kind)
      || hadKnownAuthority;
    hadKnownAuthority = false;
    const theme = boundaryChild(remoteState);
    const domainColors = boundaryChild(remoteState);
    publish(
      removeCache ? projectThemeCache(theme) : theme,
      removeCache ? projectDomainColorCache(domainColors) : domainColors,
    );
  }

  function projectAuthenticated(remoteState) {
    if (disposed) return;
    if (remoteState.kind === "signedOut") {
      activateLocal();
      return;
    }
    if (remoteState.kind === "ready") {
      authority = "account";
      publishKnown(
        remoteState.authoritativeState.theme,
        remoteState.authoritativeState.domainColors,
      );
      return;
    }
    if (remoteState.kind === "pending") {
      authority = "account";
      hadKnownAuthority = true;
      publish(
        pendingChild(
          remoteState,
          remoteState.authoritativeState.theme,
          themeState,
        ),
        pendingChild(
          remoteState,
          remoteState.authoritativeState.domainColors,
          domainColorState,
        ),
      );
      return;
    }
    if (remoteState.kind === "conflict") {
      authority = "account";
      const theme = remoteState.operation.endsWith("Theme")
        ? themeConflict(remoteState, remoteState.authoritativeState.theme)
        : remoteState.authoritativeState.theme;
      const domainColors = remoteState.operation.endsWith("DomainColors")
        ? domainColorConflict(
          remoteState,
          remoteState.authoritativeState.domainColors,
        )
        : remoteState.authoritativeState.domainColors;
      publishKnown(theme, domainColors);
      return;
    }
    projectBoundary(remoteState);
  }

  const unsubscribeTheme = localThemeRepository.subscribe((nextState) => {
    if (authority !== "local" || suppressLocalProjection || disposed) return;
    publish(projectThemeCache(nextState), domainColorState);
  });
  const unsubscribeDomainColors = localDomainColorRepository.subscribe(
    (nextState) => {
      if (authority !== "local" || suppressLocalProjection || disposed) return;
      publish(themeState, projectDomainColorCache(nextState));
    },
  );
  const unsubscribeAuthenticated = authenticatedRepository.subscribe(
    projectAuthenticated,
  );

  async function load() {
    await authenticatedRepository.load();
    return Object.freeze({ theme: themeState, domainColors: domainColorState });
  }

  async function setTheme(selection, options) {
    if (authority === "local") {
      await localThemeRepository.setSelection(selection, options);
    } else if (authority === "account") {
      await authenticatedRepository.setTheme(selection, options);
    }
    return themeState;
  }

  function selectNamedTheme(name, options) {
    return setTheme({ kind: "named", name }, options);
  }

  function followSystemTheme(options) {
    return setTheme(SYSTEM_SELECTION, options);
  }

  async function setDomainColors(mode, options) {
    if (authority === "local") {
      await localDomainColorRepository.setMode(mode, options);
    } else if (authority === "account") {
      await authenticatedRepository.setDomainColors(mode, options);
    }
    return domainColorState;
  }

  function subscribeTheme(listener) {
    if (typeof listener !== "function") {
      throw new TypeError("Theme preference listener must be a function.");
    }
    if (disposed) return () => {};
    themeListeners.add(listener);
    listener(themeState);
    return () => themeListeners.delete(listener);
  }

  function subscribeDomainColors(listener) {
    if (typeof listener !== "function") {
      throw new TypeError("Domain-color preference listener must be a function.");
    }
    if (disposed) return () => {};
    domainColorListeners.add(listener);
    listener(domainColorState);
    return () => domainColorListeners.delete(listener);
  }

  function dispose() {
    if (disposed) return;
    disposed = true;
    themeListeners.clear();
    domainColorListeners.clear();
    unsubscribeTheme();
    unsubscribeDomainColors();
    unsubscribeAuthenticated();
  }

  const theme = Object.freeze({
    decision: () => themePreferenceDecision(themeState),
    followSystemTheme,
    selectNamedTheme,
    setSelection: setTheme,
    state: () => themeState,
    subscribe: subscribeTheme,
  });
  const domainColors = Object.freeze({
    decision: () => domainColorPreferenceDecision(domainColorState),
    setMode: setDomainColors,
    state: () => domainColorState,
    subscribe: subscribeDomainColors,
  });

  return Object.freeze({ dispose, domainColors, load, theme });
}

let browserOwner = null;
let browserAuthenticatedOwner = null;
let browserWindow = null;

function browserAppearancePreferences() {
  const windowRef = globalThis.window;
  if (!windowRef || typeof windowRef !== "object") {
    throw new TypeError("Browser appearance preferences require a window.");
  }
  if (browserOwner && browserWindow !== windowRef) {
    browserOwner.dispose();
    browserAuthenticatedOwner.dispose();
    browserOwner = null;
    browserAuthenticatedOwner = null;
  }
  if (!browserOwner) {
    browserAuthenticatedOwner =
      createAuthenticatedAppearancePreferencesRepository({
        sessionRepository: browserAccountStateSessionRepository(),
        apiBaseUrl: windowRef.__fishystuffApiBaseUrl,
      });
    browserOwner = createAccountAwareAppearancePreferencesRepository({
      localThemeRepository: browserLocalThemePreferenceRepository(),
      localDomainColorRepository:
        browserLocalDomainColorPreferenceRepository(),
      authenticatedRepository: browserAuthenticatedOwner,
      themeBootCacheProvider:
        createBrowserThemeBootCacheProjectionProvider(),
      domainColorBootCacheProvider:
        createBrowserDomainColorBootCacheProjectionProvider(),
    });
    browserWindow = windowRef;
  }
  return browserOwner;
}

export function browserThemePreferenceRepository() {
  return browserAppearancePreferences().theme;
}

export function browserDomainColorPreferenceRepository() {
  return browserAppearancePreferences().domainColors;
}
